/**
 * Состояние сессии.
 *
 * Zustand, а не контекст: сессию читают и роутер, и обработчик 401, и шапка —
 * контекст заставил бы всё это быть внутри дерева React.
 */

import { create } from "zustand";

import { login as apiLogin, logout as apiLogout, me, type CurrentUser } from "@/shared/api/auth";
import { onSessionLost, setAccessToken } from "@/shared/api/client";
import {
  sanitize,
  setColors,
  setDensity,
  setSkin,
  setTheme,
  type Density,
  type Theme,
} from "@/shared/lib/theme";

export type SessionStatus = "unknown" | "authenticated" | "anonymous";

interface SessionState {
  status: SessionStatus;
  user: CurrentUser | null;
  /** Первый фактор принят, ждём код из приложения-аутентификатора. */
  awaitingSecondFactor: boolean;
  mustChangePassword: boolean;

  signIn: (
    email: string,
    password: string,
    totpCode?: string,
    accountCode?: string,
  ) => Promise<void>;
  signOut: () => Promise<void>;
  restore: () => Promise<void>;
}

/** Настройки интерфейса приезжают из профиля: человек садится за другую машину. */
function applyPreferences(user: CurrentUser): void {
  const theme = user.ui_prefs["theme"];
  const density = user.ui_prefs["density"];
  const skin = user.ui_prefs["skin"];
  if (typeof theme === "string") {
    setTheme(theme as Theme);
  }
  if (typeof density === "string") {
    setDensity(density as Density);
  }
  if (skin === "paper" || skin === "desk") {
    setSkin(skin);
  }
  // Цвета приходят с сервера: человек садится за другую машину и видит своё
  setColors(sanitize(user.ui_prefs["colors"]));
}

export const useSession = create<SessionState>((set) => {
  // Продление не удалось — возвращаемся на вход. Токен доступа при этом уже
  // недействителен, и держать человека в интерфейсе смысла нет
  onSessionLost(() => {
    setAccessToken(null);
    set({ status: "anonymous", user: null, awaitingSecondFactor: false });
  });

  return {
    status: "unknown",
    user: null,
    awaitingSecondFactor: false,
    mustChangePassword: false,

    async signIn(email, password, totpCode, accountCode) {
      const response = await apiLogin({
        email,
        password,
        ...(totpCode ? { totp_code: totpCode } : {}),
        ...(accountCode ? { account_code: accountCode } : {}),
      });

      if (response.requires_second_factor) {
        set({ awaitingSecondFactor: true });
        return;
      }

      const user = await me();
      applyPreferences(user);
      set({
        status: "authenticated",
        user,
        awaitingSecondFactor: false,
        mustChangePassword: response.must_change_password,
      });
    },

    async signOut() {
      await apiLogout();
      set({ status: "anonymous", user: null, awaitingSecondFactor: false });
    },

    /** Вкладку перезагрузили: access-токен потерян, но refresh-cookie осталась. */
    async restore() {
      try {
        const user = await me();
        applyPreferences(user);
        set({ status: "authenticated", user });
      } catch {
        setAccessToken(null);
        set({ status: "anonymous", user: null });
      }
    },
  };
});
